import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock, ChevronLeft, TrendingUp, Activity } from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const TOOLTIP_STYLE = {
  contentStyle: { background: "#111827", border: "1px solid #1F2937", borderRadius: 0, color: "#fff", fontSize: 12 },
  labelStyle: { color: "#9CA3AF" },
};

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const DailyStandup = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [form, setForm] = useState({ hours_spent: "", blockers_flag: false, summary_text: "", logged_date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pulse, m] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/pulse`),
        api.get(`/projects/${projectId}/daily-metrics?limit=30`),
      ]);
      setProject(p.data);
      setPulse(pulse.data);
      setMetrics([...m.data].reverse()); // oldest first for chart
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.hours_spent || Number(form.hours_spent) <= 0) return toast.error("Enter hours worked");
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/daily-checkin`, {
        project_id: projectId,
        hours_spent: Number(form.hours_spent),
        blockers_flag: form.blockers_flag,
        summary_text: form.summary_text || null,
        logged_date: form.logged_date,
      });
      toast.success("Check-in recorded");
      setForm({ hours_spent: "", blockers_flag: false, summary_text: "", logged_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const chartData = metrics.map((m) => ({
    date: m.logged_date,
    hours: m.hours_spent,
    blocker: m.blockers_flag ? m.hours_spent : null,
  }));

  // Running cumulative burn
  let cum = 0;
  const burnData = metrics.map((m) => {
    cum += m.hours_spent;
    return { date: m.logged_date, burned: parseFloat(cum.toFixed(1)) };
  });

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project?.name || "Project"}
        title="Daily stand-up"
        description="Log today's hours, flag blockers, and track cumulative burn."
        actions={
          <Link to={`/workspace/${projectId}`}>
            <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900">
              <ChevronLeft className="w-4 h-4 mr-1" /> Project
            </Button>
          </Link>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-6">
        {/* Pulse KPIs */}
        {pulse && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
            {[
              { label: "Hours burned", val: `${pulse.total_hours_burned ?? 0}h`, colour: "text-white" },
              { label: "Budget", val: `${pulse.allocated_hours ?? 0}h`, colour: "text-[#6B7280]" },
              { label: "SV (schedule)", val: pulse.variance?.sv != null ? `${pulse.variance.sv > 0 ? "+" : ""}${pulse.variance.sv.toFixed(0)}h` : "—", colour: (pulse.variance?.sv ?? 0) >= 0 ? "text-[#00F5D4]" : "text-[#FF007A]" },
              { label: "Blockers (7d)", val: pulse.blocker_count_last_7d ?? 0, colour: pulse.blocker_count_last_7d > 0 ? "text-[#F59E0B]" : "text-[#6B7280]" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0D1117] px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">{s.label}</div>
                <div className={`font-mono font-bold text-3xl tracking-tight mt-2 ${s.colour}`}>{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Check-in form */}
        <div className="border border-[#1F2937] bg-[#0D1117] p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Today's check-in</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Date</div>
              <input
                type="date"
                value={form.logged_date}
                onChange={(e) => setForm({ ...form, logged_date: e.target.value })}
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="checkin-date"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Hours worked</div>
              <input
                type="number"
                min="0.5"
                max="24"
                step="0.5"
                value={form.hours_spent}
                onChange={(e) => setForm({ ...form, hours_spent: e.target.value })}
                placeholder="e.g. 6"
                className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                data-testid="checkin-hours"
              />
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#9CA3AF] h-9">
                <input
                  type="checkbox"
                  checked={form.blockers_flag}
                  onChange={(e) => setForm({ ...form, blockers_flag: e.target.checked })}
                  className="accent-[#FF007A]"
                  data-testid="checkin-blocker"
                />
                <AlertTriangle className={`w-4 h-4 ${form.blockers_flag ? "text-[#FF007A]" : "text-[#374151]"}`} />
                Blocker today
              </label>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Summary / notes</div>
            <textarea
              rows={2}
              value={form.summary_text}
              onChange={(e) => setForm({ ...form, summary_text: e.target.value })}
              placeholder="What did you work on? Any context for the team…"
              className="w-full rounded-none border border-[#374151] bg-[#030712] text-white px-3 py-2 text-sm resize-none"
              data-testid="checkin-summary"
            />
          </div>
          <div className="mt-3">
            <Button
              onClick={submit}
              disabled={busy}
              className="rounded-none bg-[#002FA7] hover:bg-[#002480]"
              data-testid="checkin-submit-btn"
            >
              {busy ? "Saving…" : "Submit check-in"}
            </Button>
          </div>
        </div>

        {/* Charts */}
        {burnData.length > 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[#1F2937] bg-[#0D1117] p-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Daily hours logged</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <CartesianGrid stroke="#1F2937" vertical={false} />
                  <XAxis dataKey="date" stroke="#4B5563" fontSize={10} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis stroke="#4B5563" fontSize={10} />
                  <Tooltip {...TOOLTIP_STYLE} labelFormatter={(d) => fmtDate(d)} />
                  <Area type="monotone" dataKey="hours" stroke="#00F5D4" fill="#00F5D4" fillOpacity={0.1} strokeWidth={2} dot={false} name="Hours" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-[#1F2937] bg-[#0D1117] p-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Cumulative burn</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={burnData}>
                  <CartesianGrid stroke="#1F2937" vertical={false} />
                  <XAxis dataKey="date" stroke="#4B5563" fontSize={10} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis stroke="#4B5563" fontSize={10} />
                  <Tooltip {...TOOLTIP_STYLE} labelFormatter={(d) => fmtDate(d)} />
                  <Area type="monotone" dataKey="burned" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.1} strokeWidth={2} dot={false} name="Burned (h)" />
                  {pulse?.allocated_hours && (
                    <Area type="monotone" dataKey={() => pulse.allocated_hours} stroke="#374151" fill="none" strokeDasharray="4 4" strokeWidth={1} name="Budget" />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* History log */}
        <div className="border border-[#1F2937]">
          <div className="px-4 py-3 border-b border-[#1F2937] bg-[#111827] text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">
            Check-in history
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-[#6B7280]">Loading…</div>
          ) : metrics.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#6B7280]" data-testid="standup-empty">
              No check-ins yet. Submit your first one above.
            </div>
          ) : (
            <div className="divide-y divide-[#1F2937]">
              {[...metrics].reverse().map((m, i) => (
                <div key={i} className="flex items-start gap-4 px-5 py-3 hover:bg-[#0D1117]" data-testid={`checkin-row-${m.logged_date}`}>
                  <div className="flex-shrink-0 mt-0.5">
                    {m.blockers_flag ? (
                      <AlertTriangle className="w-4 h-4 text-[#FF007A]" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-[#00F5D4]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900">{fmtDate(m.logged_date)}</span>
                      <span className="text-xs font-mono text-[#4F46E5]">{m.hours_spent}h</span>
                      {m.blockers_flag && (
                        <span className="text-[10px] uppercase tracking-[0.15em] text-[#FF007A] bg-[#FF007A]/10 px-1.5 py-0.5">blocker</span>
                      )}
                    </div>
                    {m.summary_text && (
                      <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{m.summary_text}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyStandup;
